import { Sparkles, ArrowRight, MessageSquare } from 'lucide-react';
import { Badge } from './badge';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from '@/lib/utils/utils';
import { useViewTransitionNavigate } from '@/lib/hooks/use-view-transition';
import { useFeedback } from '@/lib/context/feedback-context';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';

interface AIDisclaimerBadgeProps {
    className?: string;
    model?: string;
    passageId?: string;
    contextType?: string;
    contentId?: string;
}

export function AIDisclaimerBadge({ className = '', model, passageId, contextType, contentId }: AIDisclaimerBadgeProps) {
    const isDarkMode = useDarkMode();
    const [, navigate] = useViewTransitionNavigate();
    const { openFeedback } = useFeedback();

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    className={cn(
                        "p-0 h-auto rounded-full hover:bg-transparent focus-visible:ring-offset-0 focus-visible:ring-ring/50 transition-all hover:scale-105 active:scale-95",
                        className
                    )}
                >
                    <Badge
                        variant="outline"
                        className={`border-border text-muted-foreground/80 gap-1.5 px-2 py-0.5 rounded-full h-5 cursor-pointer hover:bg-muted/50 hover:text-muted-foreground transition-colors ${isDarkMode ? 'bg-muted/20' : 'bg-muted/30'}`}
                    >
                        <Sparkles className="h-3 w-3 opacity-60" />
                        <span className="text-[10px] font-bold tracking-tight opacity-90">AI-CURATED</span>
                    </Badge>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-5 z-[200]">
                <div className="space-y-4">
                    <div className="flex items-start gap-4">
                        <div className="p-2 bg-muted rounded-lg shrink-0">
                            <Sparkles className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="space-y-1">
                            <h4 className="font-semibold text-sm leading-none">AI-Curated Content</h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                This content was curated and summarized by {model ? <span className="font-medium text-foreground">{model.replace('-preview', '')}</span> : 'Gemini AI'} acting as a scholarly research assistant.
                            </p>
                        </div>
                    </div>

                    <div className="bg-muted/50 rounded-lg p-3 text-xs space-y-2 border border-border/50">
                        <p className="leading-normal">
                            Our AI follows strict academic guidelines, citing only established sources and maintaining historical rigor.
                        </p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Button
                            className="w-full justify-between h-9 text-xs"
                            variant="secondary"
                            onClick={() => navigate('/app/field-guide/how-we-use-ai?hideBack=true')}
                        >
                            Read Our AI Methodology
                            <ArrowRight className="h-3.5 w-3.5 ml-2" />
                        </Button>

                        <Button
                            className="w-full justify-between h-9 text-xs"
                            variant="outline"
                            onClick={() => openFeedback({
                                passageId,
                                contextType,
                                modelInfo: model,
                                contentId
                            })}
                        >
                            <div className="flex items-center">
                                <MessageSquare className="h-3.5 w-3.5 mr-2 opacity-70" />
                                Report an Accuracy Issue
                            </div>
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}

